<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Documents</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="goTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Documents</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading documents...</p>
          </div>

          <div v-else-if="documentsData">
            <!-- Statistics Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                          <i class="mdi mdi-file-document-multiple"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Documents</p>
                        <h4 class="mb-0">{{ documentsData.statistics.totalDocuments }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                          <i class="mdi mdi-folder-multiple"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Categories</p>
                        <h4 class="mb-0">{{ documentsData.statistics.categoryCount }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                          <i class="mdi mdi-harddisk"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Size</p>
                        <h4 class="mb-0">{{ documentsData.statistics.totalSizeMB }} MB</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                          <i class="mdi mdi-clock-outline"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Latest Upload</p>
                        <h6 class="mb-0">{{ getLatestUploadDate() }}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters & Search -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-md-4">
                        <label class="form-label">Search Documents</label>
                        <div class="input-group">
                          <input
                              v-model="filters.search"
                              @input="debounceSearch"
                              type="text"
                              class="form-control"
                              placeholder="Search by name or description..."
                          >
                          <button class="btn btn-primary" type="button" @click="loadDocuments">
                            <i class="mdi mdi-magnify"></i>
                          </button>
                        </div>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Category</label>
                        <select v-model="filters.category" @change="loadDocuments" class="form-select">
                          <option value="">All Categories</option>
                          <option value="Contract">Employment Contracts</option>
                          <option value="ID">Identification Documents</option>
                          <option value="Certificate">Certificates</option>
                          <option value="Letter">Letters</option>
                          <option value="Policy">Policies</option>
                          <option value="Payslip">Payslips</option>
                          <option value="Tax">Tax Documents</option>
                          <option value="Leave">Leave Documents</option>
                          <option value="Performance">Performance Reviews</option>
                          <option value="Training">Training Materials</option>
                          <option value="Medical">Medical Documents</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div class="col-md-2">
                        <label class="form-label">View</label>
                        <select v-model="viewMode" class="form-select">
                          <option value="grid">Grid View</option>
                          <option value="list">List View</option>
                        </select>
                      </div>

                      <div class="col-md-3 d-flex align-items-end">
                        <button @click="resetFilters" class="btn btn-secondary me-2">
                          <i class="mdi mdi-refresh me-1"></i>
                          Reset
                        </button>
                        <button @click="showRequestModal" class="btn btn-success">
                          <i class="mdi mdi-plus me-1"></i>
                          Request Document
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Stats -->
            <div class="row" v-if="documentsData.categoryStats && documentsData.categoryStats.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-pie text-primary me-2"></i>
                      Documents by Category
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div v-for="(stat, index) in documentsData.categoryStats" :key="index" class="col-md-3 col-sm-6 mb-3">
                        <div class="border rounded p-3 h-100">
                          <div class="d-flex align-items-center mb-2">
                            <i :class="getCategoryIcon(stat.documentType)" class="font-size-24 text-primary me-2"></i>
                            <h6 class="mb-0">{{ stat.documentType }}</h6>
                          </div>
                          <h4 class="mb-0">{{ stat.count }}</h4>
                          <p class="text-muted mb-0 small">
                            {{ ((stat.count / documentsData.statistics.totalDocuments) * 100).toFixed(0) }}% of total
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents Grid/List View -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-file-document text-primary me-2"></i>
                      My Documents
                    </h5>
                  </div>
                  <div class="card-body">
                    <!-- Grid View -->
                    <div v-if="viewMode === 'grid'" class="row">
                      <div v-if="documentsData.documents && documentsData.documents.length > 0">
                        <div v-for="doc in documentsData.documents" :key="doc.id" class="col-xl-3 col-lg-4 col-md-6 mb-4">
                          <div class="card document-card h-100">
                            <div class="card-body text-center">
                              <div class="document-icon mb-3">
                                <i :class="getFileIcon(doc.fileType)" class="font-size-48" :style="{ color: getFileColor(doc.fileType) }"></i>
                              </div>
                              <h6 class="mb-2 text-truncate" :title="doc.name">
                                {{ doc.name }}
                              </h6>
                              <p class="text-muted mb-2 small">
                                <span class="badge bg-primary-subtle text-primary">{{ doc.documentType }}</span>
                              </p>
                              <p class="text-muted mb-2 small">
                                {{ formatFileSize(doc.fileSize) }} • {{ doc.upload_date_formatted }}
                              </p>
                              <p v-if="doc.documentNumber" class="text-muted small mb-3 description-text">
                                {{ doc.documentNumber }}
                              </p>
                              <div class="d-flex justify-content-center gap-2">
                                <button @click="viewDocument(doc)" class="btn btn-sm btn-outline-primary">
                                  <i class="mdi mdi-eye"></i> View
                                </button>
                                <button @click="downloadDocument(doc)" class="btn btn-sm btn-outline-success">
                                  <i class="mdi mdi-download"></i> Download
                                </button>
                                <button @click="confirmDelete(doc)" class="btn btn-sm btn-outline-danger">
                                  <i class="mdi mdi-delete"></i> Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center py-5">
                        <i class="mdi mdi-file-document-outline font-size-48 text-muted"></i>
                        <h5 class="mt-2">No documents found</h5>
                        <p class="text-muted">Try adjusting your filters or request a document from HR</p>
                      </div>
                    </div>

                    <!-- List View -->
                    <div v-if="viewMode === 'list'">
                      <div v-if="documentsData.documents && documentsData.documents.length > 0" class="table-responsive">
                        <table class="table table-hover align-middle mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>Document</th>
                            <th>Category</th>
                            <th>Type</th>
                            <th>Size</th>
                            <th>Uploaded</th>
                            <th class="text-center">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="doc in documentsData.documents" :key="doc.id">
                            <td>
                              <div class="d-flex align-items-center">
                                <i :class="getFileIcon(doc.fileType)" class="font-size-20 me-2" :style="{ color: getFileColor(doc.fileType) }"></i>
                                <div>
                                  <h6 class="mb-0">{{ doc.name }}</h6>
                                  <p v-if="doc.documentNumber" class="text-muted mb-0 small">{{ doc.documentNumber }}</p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span class="badge bg-primary-subtle text-primary">{{ doc.documentType }}</span>
                            </td>
                            <td>
                              <span class="text-uppercase small">{{ getFileExtension(doc.fileType) }}</span>
                            </td>
                            <td>{{ formatFileSize(doc.fileSize) }}</td>
                            <td>
                              <span class="text-muted">{{ doc.upload_date_formatted }}</span>
                            </td>
                            <td class="text-center">
                              <div class="btn-group" role="group">
                                <button @click="viewDocument(doc)" class="btn btn-sm btn-outline-primary" title="View">
                                  <i class="mdi mdi-eye"></i>
                                </button>
                                <button @click="downloadDocument(doc)" class="btn btn-sm btn-outline-success" title="Download">
                                  <i class="mdi mdi-download"></i>
                                </button>
                                <button @click="confirmDelete(doc)" class="btn btn-sm btn-outline-danger" title="Delete">
                                  <i class="mdi mdi-delete"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <div v-else class="text-center py-5">
                        <i class="mdi mdi-file-document-outline font-size-48 text-muted"></i>
                        <h5 class="mt-2">No documents found</h5>
                        <p class="text-muted">Try adjusting your filters or request a document from HR</p>
                      </div>
                    </div>

                    <!-- Pagination -->
                    <div v-if="documentsData.documents && documentsData.documents.length > 0" class="row mt-4">
                      <div class="col-sm-12 col-md-5">
                        <div class="dataTables_info">
                          Showing {{ ((documentsData.pagination.currentPage - 1) * documentsData.pagination.limit) + 1 }}
                          to {{ Math.min(documentsData.pagination.currentPage * documentsData.pagination.limit, documentsData.pagination.totalRecords) }}
                          of {{ documentsData.pagination.totalRecords }} documents
                        </div>
                      </div>
                      <div class="col-sm-12 col-md-7">
                        <div class="dataTables_paginate float-end">
                          <ul class="pagination pagination-rounded mb-0">
                            <li class="page-item" :class="{ disabled: documentsData.pagination.currentPage <= 1 }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(documentsData.pagination.currentPage - 1)">
                                <i class="mdi mdi-chevron-left"></i>
                              </a>
                            </li>
                            <li
                                v-for="page in getPageNumbers()"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === documentsData.pagination.currentPage, disabled: page === '...' }">
                              <a
                                  v-if="page !== '...'"
                                  class="page-link"
                                  href="javascript:void(0)"
                                  @click="changePage(page)">
                                {{ page }}
                              </a>
                              <span v-else class="page-link">{{ page }}</span>
                            </li>
                            <li class="page-item" :class="{ disabled: documentsData.pagination.currentPage >= documentsData.pagination.totalPages }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(documentsData.pagination.currentPage + 1)">
                                <i class="mdi mdi-chevron-right"></i>
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Request Document Modal -->
    <div class="modal fade" id="requestDocumentModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="mdi mdi-file-upload me-2"></i>
              Request Document Upload
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitDocumentRequest">
              <div class="mb-3">
                <label class="form-label">Document Type *</label>
                <select v-model="documentRequest.type" class="form-select" required>
                  <option value="">Select document type</option>
                  <option value="Contract">Employment Contract</option>
                  <option value="ID">ID Document</option>
                  <option value="Certificate">Certificate</option>
                  <option value="Letter">Letter</option>
                  <option value="Tax">Tax Document</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label">Document Description *</label>
                <input
                    v-model="documentRequest.description"
                    type="text"
                    class="form-control"
                    placeholder="E.g., Updated Employment Contract"
                    required
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Reason for Request</label>
                <textarea
                    v-model="documentRequest.reason"
                    class="form-control"
                    rows="3"
                    placeholder="Why do you need this document?"
                ></textarea>
              </div>

              <div class="alert alert-info mb-0">
                <i class="mdi mdi-information me-2"></i>
                <small>Your request will be sent to HR for processing. You'll be notified once the document is uploaded.</small>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" @click="submitDocumentRequest" class="btn btn-primary" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
              Submit Request
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteConfirmModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-alert-circle me-2"></i>
              Confirm Deletion
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="documentToDelete">
            <div class="text-center mb-3">
              <i class="mdi mdi-delete-alert font-size-48 text-danger"></i>
            </div>
            <p class="text-center mb-3">
              Are you sure you want to delete this document?
            </p>
            <div class="alert alert-warning">
              <h6 class="mb-2">
                <i :class="getFileIcon(documentToDelete.fileType)" class="me-2"></i>
                {{ documentToDelete.name }}
              </h6>
              <p class="mb-0 small">
                <strong>Type:</strong> {{ documentToDelete.documentType }}<br>
                <strong>Size:</strong> {{ formatFileSize(documentToDelete.fileSize) }}
              </p>
            </div>
            <div class="alert alert-danger">
              <i class="mdi mdi-information me-2"></i>
              <strong>Warning:</strong> This action cannot be undone. The document will be permanently removed from your profile.
            </div>

            <!-- Type DELETE to confirm -->
            <div class="mb-3">
              <label class="form-label">
                <strong>Type <code class="text-danger">DELETE</code> to confirm:</strong>
              </label>
              <input
                  v-model="deleteConfirmText"
                  type="text"
                  class="form-control"
                  placeholder="Type DELETE here"
                  @input="deleteConfirmText = deleteConfirmText.toUpperCase()"
                  :class="{ 'is-valid': isDeleteConfirmed, 'is-invalid': deleteConfirmText && !isDeleteConfirmed }"
              >
              <div v-if="deleteConfirmText && !isDeleteConfirmed" class="invalid-feedback">
                Please type exactly "DELETE" to confirm
              </div>
              <div v-if="isDeleteConfirmed" class="valid-feedback">
                Confirmed! You can now delete the document
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetDeleteConfirm">
              <i class="mdi mdi-close me-1"></i>
              Cancel
            </button>
            <button
                type="button"
                @click="executeDelete"
                class="btn btn-danger"
                :disabled="deleting || !isDeleteConfirmed">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-delete me-1"></i>
              {{ deleting ? 'Deleting...' : 'Delete Document' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute, uploads } from "@/api";

export default {
  name: "EmployeeDocumentsView",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      submitting: false,
      deleting: false,
      documentsData: null,
      filters: {
        category: '',
        search: '',
        page: 1
      },
      viewMode: 'grid',
      searchTimeout: null,
      documentToDelete: null,
      deleteModal: null,
      deleteConfirmText: '',
      documentRequest: {
        type: '',
        description: '',
        reason: ''
      }
    };
  },

  computed: {
    isDeleteConfirmed() {
      return this.deleteConfirmText === 'DELETE';
    }
  },

  mounted() {
    this.loadDocuments();
  },

  methods: {
    async loadDocuments() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeDocuments");
        data.append("page", this.filters.page);
        data.append("limit", 50);

        if (this.filters.category) {
          data.append("category", this.filters.category);
        }

        if (this.filters.search) {
          data.append("search", this.filters.search);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.documentsData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load documents');
        }
      } catch (error) {
        this.$toast?.error("Failed to load documents: " + error.message) ||
        console.error("Failed to load documents:", error);
      } finally {
        this.loading = false;
      }
    },

    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.filters.page = 1;
        this.loadDocuments();
      }, 500);
    },

    resetFilters() {
      this.filters = {
        category: '',
        search: '',
        page: 1
      };
      this.loadDocuments();
    },

    changePage(page) {
      if (page < 1 || page > this.documentsData.pagination.totalPages || page === '...') return;
      this.filters.page = page;
      this.loadDocuments();
    },

    getPageNumbers() {
      const totalPages = this.documentsData.pagination.totalPages;
      const currentPage = this.documentsData.pagination.currentPage;

      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      if (currentPage <= 3) {
        return [1, 2, 3, 4, '...', totalPages];
      }

      if (currentPage >= totalPages - 2) {
        return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      }

      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    },

    async downloadDocument(doc) {
      if (doc.filePath) {
        // Open document directly using the uploads base URL
        window.open(uploads + '' + doc.filePath, '_blank');

        this.$toast?.success("Document opened successfully") ||
        console.log("Document opened");

        // Log the download in background (optional)
        try {
          const data = new FormData();
          data.append("function", "downloadEmployeeDocument");
          data.append("document_id", doc.id);
          await execute(data, authUrl);
        } catch (error) {
          // Silent fail for logging
          console.log("Download logging failed:", error);
        }
      } else {
        this.$toast?.error("Document file not available") ||
        alert("Document file not available");
      }
    },

    viewDocument(doc) {
      // Same as download - opens in new tab
      this.downloadDocument(doc);
    },

    confirmDelete(doc) {
      this.documentToDelete = doc;
      this.deleteConfirmText = ''; // Reset confirmation text

      // Initialize modal if not already done
      if (!this.deleteModal) {
        this.$nextTick(() => {
          const modalElement = document.getElementById('deleteConfirmModal');
          if (modalElement) {
            this.deleteModal = new bootstrap.Modal(modalElement);
          }
        });
      }

      // Show modal
      this.$nextTick(() => {
        if (this.deleteModal) {
          this.deleteModal.show();
        }
      });
    },

    async executeDelete() {
      if (!this.documentToDelete || !this.isDeleteConfirmed) return;

      this.deleting = true;

      try {
        const data = new FormData();
        data.append("function", "deleteEmployeeDocument");
        data.append("document_id", this.documentToDelete.id);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Document deleted successfully") ||
          alert("Document deleted successfully");

          // Hide modal
          if (this.deleteModal) {
            this.deleteModal.hide();
          }

          // Reset
          this.resetDeleteConfirm();

          // Reload documents
          this.loadDocuments();
        } else {
          throw new Error(res.data?.message || 'Delete failed');
        }
      } catch (error) {
        this.$toast?.error("Delete failed: " + error.message) ||
        alert("Delete failed: " + error.message);
      } finally {
        this.deleting = false;
      }
    },

    resetDeleteConfirm() {
      this.documentToDelete = null;
      this.deleteConfirmText = '';
    },

    showRequestModal() {
      this.documentRequest = {
        type: '',
        description: '',
        reason: ''
      };

      const modal = new bootstrap.Modal(document.getElementById('requestDocumentModal'));
      modal.show();
    },

    async submitDocumentRequest() {
      if (!this.documentRequest.type || !this.documentRequest.description) {
        this.$toast?.error("Please fill in all required fields") ||
        alert("Please fill in all required fields");
        return;
      }

      this.submitting = true;

      try {
        const data = new FormData();
        data.append("function", "requestDocumentUpload");
        data.append("document_type", this.documentRequest.type);
        data.append("description", this.documentRequest.description);
        data.append("reason", this.documentRequest.reason);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Document request submitted successfully") ||
          alert("Document request submitted successfully");

          const modal = bootstrap.Modal.getInstance(document.getElementById('requestDocumentModal'));
          modal.hide();

          // Reset form
          this.documentRequest = {
            type: '',
            description: '',
            reason: ''
          };
        } else {
          throw new Error(res.data?.message || 'Request failed');
        }
      } catch (error) {
        this.$toast?.error("Request failed: " + error.message) ||
        alert("Request failed: " + error.message);
      } finally {
        this.submitting = false;
      }
    },

    getFileIcon(fileType) {
      const type = fileType?.toLowerCase() || '';

      if (type.includes('pdf')) return 'mdi mdi-file-pdf-box';
      if (type.includes('doc')) return 'mdi mdi-file-word';
      if (type.includes('xls') || type.includes('sheet')) return 'mdi mdi-file-excel';
      if (type.includes('ppt') || type.includes('presentation')) return 'mdi mdi-file-powerpoint';
      if (type.includes('image') || type.includes('jpg') || type.includes('png')) return 'mdi mdi-file-image';
      if (type.includes('zip') || type.includes('rar')) return 'mdi mdi-folder-zip';

      return 'mdi mdi-file-document';
    },

    getFileExtension(fileType) {
      const type = fileType?.toLowerCase() || '';

      if (type.includes('pdf')) return 'PDF';
      if (type.includes('msword')) return 'DOC';
      if (type.includes('wordprocessingml')) return 'DOCX';
      if (type.includes('excel') || type.includes('spreadsheet')) return 'XLSX';
      if (type.includes('jpeg')) return 'JPG';
      if (type.includes('png')) return 'PNG';

      return 'FILE';
    },

    getFileColor(fileType) {
      const type = fileType?.toLowerCase() || '';

      if (type.includes('pdf')) return '#d32f2f';
      if (type.includes('doc')) return '#2196f3';
      if (type.includes('xls')) return '#4caf50';
      if (type.includes('ppt')) return '#ff9800';
      if (type.includes('image')) return '#9c27b0';
      if (type.includes('zip')) return '#795548';

      return '#607d8b';
    },

    getCategoryIcon(category) {
      const icons = {
        'Contract': 'mdi mdi-file-document',
        'ID': 'mdi mdi-card-account-details',
        'Certificate': 'mdi mdi-certificate',
        'Letter': 'mdi mdi-email',
        'Policy': 'mdi mdi-file-check',
        'Payslip': 'mdi mdi-cash',
        'Tax': 'mdi mdi-calculator',
        'Leave': 'mdi mdi-calendar-account',
        'Performance': 'mdi mdi-chart-line',
        'Training': 'mdi mdi-school',
        'Medical': 'mdi mdi-medical-bag',
        'Other': 'mdi mdi-file'
      };

      return icons[category] || 'mdi mdi-file';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';

      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));

      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
    },

    getLatestUploadDate() {
      if (!this.documentsData.recentDocuments || this.documentsData.recentDocuments.length === 0) {
        return 'N/A';
      }

      return this.documentsData.recentDocuments[0].upload_date_formatted;
    },

    goTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.font-size-48 {
  font-size: 48px;
}

.font-size-24 {
  font-size: 24px;
}

.font-size-20 {
  font-size: 20px;
}

.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.03);
}

.document-card {
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.document-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
  border-color: #5b73e8;
}

.description-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  max-height: 2.8em;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>